import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCpu, FiTrendingUp, FiZap, FiShield, FiBarChart, FiTarget, FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import { useSEO } from '../hooks/useSEO';
import './APM.css';

const AIMonitoring = () => {
  useSEO({
    title: 'AI Monitoring Tools - Intelligent Observability & Analytics | LogCrest',
    description: 'AI-powered monitoring tools for modern infrastructure. Intelligent log analysis, predictive alerting, and automated incident response. Advanced AI monitoring for cloud-native applications.',
    keywords: 'AI monitoring, AI observability tool, AI powered log analysis, AI monitoring tools, intelligent monitoring, AI-powered analytics, machine learning monitoring, predictive monitoring'
  });

  useEffect(() => {
    trackPageView('/ai-monitoring', {
      page_title: 'LogCrest AI Monitoring',
      page_category: 'ai-monitoring'
    });
  }, []);

  const features = [
    {
      icon: <FiCpu />,
      title: 'AI-Powered Log Analysis',
      description: 'Advanced AI algorithms analyze log data to identify patterns, anomalies, and root causes automatically.'
    },
    {
      icon: <FiTarget />,
      title: 'Predictive Monitoring',
      description: 'Machine learning models predict potential issues before they impact your systems.'
    },
    {
      icon: <FiZap />,
      title: 'Intelligent Alerting',
      description: 'AI reduces alert fatigue by filtering false positives and prioritizing critical issues.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Automated Root Cause Analysis',
      description: 'AI automatically correlates events and identifies root causes of incidents.'
    },
    {
      icon: <FiBarChart />,
      title: 'Intelligent Analytics',
      description: 'Get actionable insights from your monitoring data with AI-powered analytics.'
    },
    {
      icon: <FiShield />,
      title: 'Anomaly Detection',
      description: 'Detect unusual patterns and behaviors across your infrastructure automatically.'
    }
  ];

  const benefits = [
    'AI-powered log analysis with pattern recognition',
    'Predictive monitoring to prevent issues',
    'Intelligent alerting reduces false positives by 90%',
    'Automated root cause analysis',
    'Machine learning monitoring for continuous improvement',
    'AI-powered analytics for actionable insights'
  ];

  return (
    <div className="agent-sre-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>AI Monitoring Tools</h1>
            <p className="hero-subtitle">
              Intelligent observability and analytics powered by AI. Advanced AI monitoring for cloud-native applications with predictive insights.
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                  <FiArrowRight />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>AI Observability Tool</h2>
            <p>Intelligent monitoring powered by advanced machine learning</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose AI Monitoring Tools?</h2>
              <p>
                Our AI observability tool provides intelligent monitoring with predictive insights 
                and automated analysis for modern cloud-native applications.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>AI Monitoring Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiCpu />
                    <span>AI Accuracy: 99.9%</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>Predictions: 1.2K/day</span>
                  </div>
                  <div className="metric">
                    <FiBarChart />
                    <span>Anomalies Detected: 45</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for AI-Powered Monitoring?</h2>
            <p>Experience intelligent monitoring with AI-powered analytics and predictive insights.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
                <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIMonitoring;

